import { useState, useEffect, type FormEvent } from "react";
import { Dialog } from "@headlessui/react";
import axios from "axios";
import { getToken } from "utils/getToken";
import { toast } from "react-toastify";
import SyncLoader from "components/Loaders/SyncLoader";
import { IoMdClose } from "react-icons/io";
import { Listbox } from "@headlessui/react";
import { HiOutlineSelector } from "react-icons/hi";
import { MdOutlineClose } from "react-icons/md";
import "react-toastify/dist/ReactToastify.css";
import {
  fieldLabel,
  focusRing,
  input as inputCls,
  primaryBtn,
  select as selectCls,
} from "components/ui/instrument";

const headers = {
  "Content-Type": "application/json",
  Authorization: `Token ${getToken()}`,
};

const roleOptions = [
  { name: "User" },
  { name: "Doctor" },
  { name: "Affiliate" },
  { name: "Support" },
  { name: "Sales" },
];

interface EditUserModalProps {
  openEditUserModal: any;
  setOpenEditUserModal: (state: any) => void;
  refresh: () => void;
}

const EditUserModal = ({
  openEditUserModal,
  setOpenEditUserModal,
  refresh,
}: EditUserModalProps) => {
  const [loading, setLoading] = useState(false);
  const [supportUsers, setSupportUsers] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    firstName: openEditUserModal.firstName,
    lastName: openEditUserModal.lastName,
    roles: openEditUserModal.role || [], // Ensure roles is always an array
    phoneNumber: openEditUserModal.userMobile,
    email: openEditUserModal.email,
    growthCallEnabled: openEditUserModal.growth,
    subscriberOnboarding: openEditUserModal.onboardingStatus ? "Yes" : "No",
    btpSubscriber: openEditUserModal.btpSubscriber,
    updatedBy: openEditUserModal.updated_by || "",
  });

  useEffect(() => {
    if (openEditUserModal.open) {
      getSupportUsers();

      setFormData({
        firstName: openEditUserModal.firstName,
        lastName: openEditUserModal.lastName,
        roles: openEditUserModal.role?.filter((role: any) => role !== null) || [],
        phoneNumber: openEditUserModal.userMobile,
        email: openEditUserModal.email,
        growthCallEnabled: openEditUserModal.growth,
        subscriberOnboarding: openEditUserModal.onboardingStatus ? "Yes" : "No",
        btpSubscriber: openEditUserModal.btpSubscriber,
        updatedBy: openEditUserModal.updated_by || "",
      });
    }
  }, [openEditUserModal]);

  const handleEditUser = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (openEditUserModal.userId) {
      try {
        setLoading(true);

        // Prepare the payload
        const payload = {
          id: openEditUserModal.userId,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone_number: formData.phoneNumber,
          email: formData.email,
          growth_call_enabled: formData.growthCallEnabled === "Yes",
          subscriber_onboarding: {
            call_completed: formData.subscriberOnboarding === "Yes", // Convert "Yes"/"No" back to boolean
            call_updated_by: formData.updatedBy,
          },
          user_role: formData.roles,
        };

        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}users/update-user/`,
          payload,
          { headers }
        );

        if (response.status === 200) {
          toast.success("User details updated successfully!");
          setOpenEditUserModal({ open: false, userId: null });
          refresh();
        }
      } catch (error) {
        toast.error("Something went wrong while editing user data.");
        console.error("Edit user data", error);
      } finally {
        setLoading(false);
      }
    } else {
      toast.warn("Invalid user ID. Please try again.");
    }
  };

  const handleRoleChange = (selectedRoles: any) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      roles: selectedRoles,
    }));
  };

  const handleOnboardingUserChange = (userId: any) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      updatedBy: userId,
    }));
  };

  const closeModal = () => {
    setOpenEditUserModal({
      open: false,
      userId: null,
    });
  };

  const getSupportUsers = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}users/get-support-user/`,
      { headers }
    );
    setSupportUsers(res.data);
  };

  return (
    <Dialog
      open={openEditUserModal.open}
      onClose={closeModal}
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto"
    >
      <div
        className="bg-black fixed inset-0 bg-opacity-50"
        aria-hidden="true"
      />
      <div className="relative mx-auto w-full max-w-lg rounded-lg border border-clinic-200 bg-white shadow-lg">
        <div className="flex items-center justify-between border-b border-clinic-200 p-4">
          <h3 className="text-base font-bold text-clinic-900">Edit User</h3>
          <button
            onClick={closeModal}
            aria-label="Close"
            className={`rounded text-clinic-500 hover:text-clinic-700 ${focusRing}`}
          >
            <IoMdClose size={25} />
          </button>
        </div>

        <form onSubmit={handleEditUser} className="space-y-6 p-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={fieldLabel}>
                First Name
              </label>
              <input
                name="firstName"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                className={`mt-1 ${inputCls}`}
              />
            </div>
            <div>
              <label className={fieldLabel}>
                Last Name
              </label>
              <input
                name="lastName"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                className={`mt-1 ${inputCls}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={fieldLabel}>
                Role
              </label>
              <Listbox
                value={formData.roles}
                onChange={handleRoleChange}
                multiple
              >
                {({ open }) => (
                  <>
                    <div className="relative">
                      <Listbox.Button className={`inline-flex w-full items-center justify-between gap-2 rounded-md border border-clinic-300 bg-white px-2 py-3 text-sm font-medium text-clinic-700 shadow-sm hover:bg-clinic-50 ${focusRing}`}>
                        {Array.isArray(formData.roles) &&
                        formData.roles.length > 0
                          ? `${formData.roles.length} Role(s) Selected`
                          : "Select Roles"}
                        {Array.isArray(formData.roles) &&
                        formData.roles.length > 0 ? (
                          <button
                            onClick={() => handleRoleChange([])}
                            aria-label="Clear selected roles"
                            className={`ml-2 flex items-center rounded text-clinic-500 hover:text-crit-600 ${focusRing}`}
                          >
                            <MdOutlineClose size={22} />
                          </button>
                        ) : (
                          <HiOutlineSelector size={22} />
                        )}
                      </Listbox.Button>

                      <Listbox.Options
                        className={`absolute mt-1 max-h-60 min-w-full overflow-auto rounded-md border border-clinic-200 bg-white py-1 text-base shadow-lg focus:outline-none sm:text-sm ${
                          open ? "block" : "hidden"
                        }`}
                      >
                        {roleOptions.map((option, index) => (
                          <Listbox.Option
                            key={index}
                            value={option.name} // Send only the role name
                            className={({ active }) =>
                              `${
                                active
                                  ? "bg-clinic-100 text-clinic-900"
                                  : "text-clinic-700"
                              } relative cursor-pointer select-none py-2 pl-3 pr-9`
                            }
                          >
                            {({ selected }) => (
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  readOnly
                                  checked={selected}
                                  className="form-checkbox h-5 w-5 text-action-600"
                                />
                                <span
                                  className={`ml-3 block truncate ${
                                    selected ? "font-medium" : "font-normal"
                                  }`}
                                >
                                  {option.name}
                                </span>
                              </div>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </div>
                  </>
                )}
              </Listbox>
            </div>

            <div>
              <label className={fieldLabel}>
                Phone Number
              </label>
              <input
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) =>
                  setFormData({ ...formData, phoneNumber: e.target.value })
                }
                className={`mt-1 tabular-nums ${inputCls}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={fieldLabel}>
                Email
              </label>
              <input
                name="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className={`mt-1 ${inputCls}`}
              />
            </div>
            <div>
              <label className={fieldLabel}>
                Growth Call Enabled
              </label>
              <select
                name="growthCallEnabled"
                value={formData.growthCallEnabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    growthCallEnabled: e.target.value,
                  })
                }
                className={`mt-1 w-full ${selectCls}`}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>

          {/* Conditionally render Onboarding fields if btpSubscriber is true */}
          {formData.btpSubscriber && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={fieldLabel}>
                  Onboarding Status
                </label>
                <select
                  name="onboardingStatus"
                  value={formData.subscriberOnboarding}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      subscriberOnboarding: e.target.value,
                    })
                  }
                  className={`mt-1 w-full ${selectCls}`}
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              <div>
                <label className={fieldLabel}>
                  Done by
                </label>
                <select
                  name="updatedBy"
                  value={formData.updatedBy}
                  onChange={(e) => handleOnboardingUserChange(e.target.value)}
                  className={`mt-1 w-full ${selectCls}`}
                >
                  <option value="">Select User</option>
                  {supportUsers.map((userInfo) => (
                    <option key={userInfo.id} value={userInfo.id}>
                      {userInfo.name || userInfo.email}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            {loading ? (
              <div className="flex justify-center py-2">
                <SyncLoader size="8px" color="#0e7490" />
              </div>
            ) : (
              <button type="submit" className={primaryBtn}>
                Update User
              </button>
            )}
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default EditUserModal;
